import React from 'react';
import dayjs from 'dayjs';
import Button from '../../components/buttons/Button';
import ModalCloseButton from '../../components/modal/ModalCloseButton';
import {
  PREPARING_MESSAGE,
  useReportDownload,
} from '../../hooks/useReportDownload';

interface ModalContentProps {
  closeModal: (reset: boolean) => void;
  startDate: Date | null;
  endDate: Date | null;
  lounges: string[];
  allLounge: [
    {
      name: string;
      id: string;
      location: string;
    },
  ];
  reportType: string;
}

const ModalContent: React.FC<ModalContentProps> = ({
  closeModal,
  startDate,
  endDate,
  lounges,
  allLounge,
  reportType,
}) => {
  const { modalRef, loading, error, close, download } = useReportDownload({
    onClose: () => closeModal(false),
  });

  const handleDownload = async () => {
    const isVoucher = reportType === 'vouchers';
    const filename = `${isVoucher ? 'Redeem-Vouchers' : 'transactions'}-${
      startDate ? dayjs(startDate).format('DD-MM-YYYY') : ''
    }-${endDate ? dayjs(endDate).format('DD-MM-YYYY') : ''}.csv`;
    const saved = await download({
      endpoint: isVoucher
        ? 'v1/voucher/voucherReport'
        : 'v1/lounge/transactions',
      filename,
      body: {
        excel: true,
        start_date: startDate ? dayjs(startDate).format('YYYY-MM-DD') : null,
        end_date: endDate ? dayjs(endDate).format('YYYY-MM-DD') : null,
        lounge_ids: lounges,
        ...(isVoucher ? {} : { report_type: reportType }),
      },
    });
    if (saved) closeModal(true);
  };

  const loungeNames =
    lounges
      ?.map((item) => allLounge.find((option) => option.id === item)?.name)
      .join(', ') || '';

  const displayNames =
    loungeNames.length > 500 ? loungeNames.slice(0, 500) + '...' : loungeNames;

  return (
    <div
      ref={modalRef}
      data-testid="report-download-modal"
      className="relative flex h-[464px] w-[856px] flex-col rounded-[12px] border bg-white"
      style={{ border: '1px solid rgba(229, 231, 235, 1)' }}
    >
      <ModalCloseButton onClick={close} testId="report-modal-close-button" />
      <div className="flex flex-1 flex-col items-center justify-center">
        <div className="text-3xl font-semibold text-[#1E429F]">
          Ready to download?
        </div>
        <div className="mt-6 text-center">
          You have requested transactions from{' '}
          <span className="font-semibold text-[#1C64F2]">
            {startDate ? dayjs(startDate).format('DD-MM-YYYY') : ''} to{' '}
            {endDate ? dayjs(endDate).format('DD-MM-YYYY') : ''}{' '}
          </span>
          for
          <div
            className="mb-6 px-6 font-semibold text-[#1C64F2]"
            style={{
              maxWidth: '100%',
              wordBreak: 'break-word', // Ensure long words break
              overflowWrap: 'break-word', // Break lines within long content
              overflow: 'hidden', // Prevent overflow
              textOverflow: 'ellipsis', // Add ellipsis for truncation
            }}
          >
            {displayNames}
          </div>
          <div className="inline-table w-[500px]">
            <Button
              loading={loading}
              text="Confirm & Download Report"
              onClick={handleDownload}
              data-testid="report-confirm-download-button"
            />
            {loading && (
              <p role="status" className="mt-3 text-sm text-gray-600">
                {PREPARING_MESSAGE}
              </p>
            )}
            {error && (
              <p role="alert" className="mt-3 text-sm text-red-600">
                {error}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModalContent;
